import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { I18n } from 'react-redux-i18n';
import { Accordion, Button, Message, Modal, Table } from 'semantic-ui-react';
import { CheckCircle2, MinusCircle, XCircle } from 'lucide-react';
import {
  deidentify,
  previewDeidentify,
  revertDeidentify
} from './retentionApi';
import {
  DeidentifyOutcome,
  DeidentifyResult,
  DeidentifyTableCount,
  deidentifyOutcome
} from './types';
import { i18nKeys } from './constants';
import styles from './DeidentifyModal.module.css';

const i18nRoot = i18nKeys.DEIDENTIFY_ROOT;

export interface DeidentifyModalSubject {
  accountId: number | null;
  email: string;
  /** Flag row id. Absent on the account detail page, where the subject may never have been flagged. */
  id?: number;
}

export type DeidentifyMode = 'deidentify' | 'revert';

interface DeidentifyModalProps {
  mode: DeidentifyMode;
  onClose: () => void;
  /** Called once something has actually been written, so the caller can refresh its grid. */
  onCompleted: () => void;
  subjects: DeidentifyModalSubject[];
}

const outcomeIcon = (outcome: DeidentifyOutcome) => {
  if (outcome === deidentifyOutcome.skipped) {
    return (
      <MinusCircle
        size={16}
        color="var(--color-text-secondary, #757575)"
        aria-hidden
      />
    );
  }
  if (outcome === deidentifyOutcome.refused) {
    return (
      <XCircle size={16} color="var(--color-error, #d32f2f)" aria-hidden />
    );
  }
  return (
    <CheckCircle2 size={16} color="var(--color-success, #2e7d32)" aria-hidden />
  );
};

// react-redux-i18n has no plural handling, so the form is chosen here rather than by the library.
const rowLabel = (rows: number) =>
  I18n.t(rows === 1 ? `${i18nRoot}.rowsOne` : `${i18nRoot}.rowsOther`, {
    count: rows
  });

/** What one table would be changed to, shown when a subject is expanded. */
const TableDetail = ({ table }: { table: DeidentifyTableCount }) => (
  <div className={styles.detail}>
    <strong>{table.name}</strong> <span>{rowLabel(table.rows)}</span>
    {table.columns && table.columns.length > 0 && (
      <Table basic="very" compact size="small" unstackable>
        <Table.Body>
          {table.columns.map((column) => (
            <Table.Row key={column.name}>
              <Table.Cell className={styles.columnName}>
                {column.name}
              </Table.Cell>
              <Table.Cell className={styles.columnValue} textAlign="right">
                {column.value === null ? (
                  <em>{I18n.t(`${i18nRoot}.cleared`)}</em>
                ) : (
                  <code>{column.value}</code>
                )}
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    )}
  </div>
);

/**
 * Confirmation for de-identifying flagged subjects, and for undoing one inside the revert window.
 *
 * De-identification runs a dry-run preview first and only enables the confirm button for subjects the
 * API reports as eligible, so the operator sees exactly which records would change before anything is
 * written. Revert skips the preview: the API refuses once the snapshot has been purged, and that
 * refusal is the answer.
 */
const DeidentifyModal = ({
  mode,
  onClose,
  onCompleted,
  subjects
}: DeidentifyModalProps) => {
  const isRevert = mode === 'revert';
  const [preview, setPreview] = useState<DeidentifyResult | null>(null);
  const [results, setResults] = useState<DeidentifyResult | null>(null);
  const [isLoading, setIsLoading] = useState(!isRevert);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Index of the expanded subject, or null. One at a time keeps the modal short for a full batch.
  const [expanded, setExpanded] = useState<number | null>(null);

  // Flag ids where we have them (the retention grid), account ids otherwise (the account detail
  // page, where the subject may have no flag row at all). Both resolve to the same email server side.
  const request = useMemo(() => {
    const flagIds = subjects
      .map((s) => s.id)
      .filter((id): id is number => id != null);
    const accountIds = subjects
      .filter((s) => s.id == null && s.accountId != null)
      .map((s) => s.accountId as number);
    return {
      ...(flagIds.length > 0 ? { flagIds } : {}),
      ...(accountIds.length > 0 ? { accountIds } : {})
    };
  }, [subjects]);

  useEffect(() => {
    if (isRevert) {
      return;
    }
    let cancelled = false;
    previewDeidentify(request)
      .then((result) => {
        if (!cancelled) {
          setPreview(result);
          setIsLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(I18n.t(`${i18nRoot}.previewError`));
          setIsLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [request, isRevert]);

  const eligible = useMemo(
    () =>
      (preview?.subjects || []).filter(
        (s) => s.outcome === deidentifyOutcome.eligible
      ),
    [preview]
  );

  const handleConfirm = useCallback(async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      const call = isRevert ? revertDeidentify : deidentify;
      const result = await call(request);
      setResults(result);
      // Something was written even if some subjects were refused, so the grid is stale either way.
      onCompleted();
    } catch {
      setError(I18n.t(`${i18nRoot}.submitError`));
    } finally {
      setIsSubmitting(false);
    }
  }, [request, isRevert, onCompleted]);

  const shown = results || preview;
  const isDone = results !== null;
  const canConfirm = isRevert || eligible.length > 0;

  return (
    <Modal open onClose={onClose} size="small" closeIcon>
      <Modal.Header>
        {I18n.t(isRevert ? `${i18nRoot}.revertTitle` : `${i18nRoot}.title`)}
      </Modal.Header>
      <Modal.Content>
        <div aria-live="polite">
          {!isDone && (
            <p>
              {I18n.t(
                isRevert ? `${i18nRoot}.revertIntro` : `${i18nRoot}.intro`,
                {
                  count: subjects.length
                }
              )}
            </p>
          )}

          {!isRevert && !isDone && preview && (
            <Message warning size="small">
              {I18n.t(`${i18nRoot}.warning`, {
                days: preview.revertWindowDays
              })}
            </Message>
          )}

          {error && (
            <Message negative size="small">
              {error}
            </Message>
          )}

          {isLoading && <p>{I18n.t(`${i18nRoot}.loading`)}</p>}

          {shown && (
            <>
              <h5>
                {I18n.t(
                  isDone ? `${i18nRoot}.resultHeading` : `${i18nRoot}.subjects`
                )}
              </h5>
              <Accordion styled fluid>
                {(shown.subjects || []).map((subject, index) => {
                  const tables = subject.tables || [];
                  const isOpen = expanded === index;
                  return (
                    <React.Fragment key={subject.email}>
                      <Accordion.Title
                        active={isOpen}
                        index={index}
                        onClick={() => setExpanded(isOpen ? null : index)}
                        aria-expanded={isOpen}
                      >
                        <span className={styles.title}>
                          {outcomeIcon(subject.outcome)}
                          <span className={styles.titleEmail}>
                            {subject.email}
                          </span>
                          <span className={styles.titleOutcome}>
                            {I18n.t(`${i18nRoot}.outcome.${subject.outcome}`)}
                          </span>
                        </span>
                      </Accordion.Title>
                      <Accordion.Content active={isOpen}>
                        {subject.reason && (
                          <Message info size="small">
                            {subject.reason}
                          </Message>
                        )}
                        {tables.length === 0 ? (
                          <p>{I18n.t(`${i18nRoot}.noTables`)}</p>
                        ) : (
                          <>
                            <h5>{I18n.t(`${i18nRoot}.columns`)}</h5>
                            {tables.map((table) => (
                              <TableDetail key={table.name} table={table} />
                            ))}
                          </>
                        )}
                      </Accordion.Content>
                    </React.Fragment>
                  );
                })}
              </Accordion>

              {!isDone && !canConfirm && (
                <Message info size="small">
                  {I18n.t(`${i18nRoot}.nothingToDo`)}
                </Message>
              )}
            </>
          )}
        </div>

        <div
          className="button-wrapper align-right"
          style={{ marginTop: '16px' }}
        >
          {isDone ? (
            <Button primary onClick={onClose}>
              {I18n.t(`${i18nRoot}.close`)}
            </Button>
          ) : (
            <>
              <Button onClick={onClose} disabled={isSubmitting}>
                {I18n.t(`${i18nRoot}.cancel`)}
              </Button>
              <Button
                negative={!isRevert}
                primary={isRevert}
                onClick={handleConfirm}
                loading={isSubmitting}
                disabled={isSubmitting || isLoading || !canConfirm}
              >
                {I18n.t(
                  isRevert ? `${i18nRoot}.confirmRevert` : `${i18nRoot}.confirm`
                )}
              </Button>
            </>
          )}
        </div>
      </Modal.Content>
    </Modal>
  );
};

export default DeidentifyModal;
